import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { FormControl, Grid, InputLabel } from '@mui/material'
import * as R from 'ramda'
import { type Field, PuiSelect, PuiTextField } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import {
  useGetCompoundingReasonTypeById,
  useGetIsCompoundingReasonDetailsRequired,
} from '~/store/hooks/prescription'
import {
  getFeatureToggle,
  getPrescriptionCompoundingReason,
} from '~/store/reducers/constants'
import {
  DOSAGE_FORM_ID,
  DOSAGE_FORM_V2_ID,
} from '~/types/entities/prescriptions'

interface CompoundingReasonProps {
  compoundingReasonField: Field
  compoundingReasonIdField: Field
}

const CompoundingReason = ({
  compoundingReasonField,
  compoundingReasonIdField,
}: CompoundingReasonProps) => {
  const { t } = useTranslation('Common')

  const prescriptionCompoundingReason = useSelector(
    getPrescriptionCompoundingReason,
  )
  const isRhapUpdateReasonForCompoundingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.RHAP_UPDATE_REASON_FOR_COMPOUNDING),
  )

  const { isAllergiesIntolerances } = useGetCompoundingReasonTypeById()(
    compoundingReasonIdField.value,
  )
  const isCompoundingReasonDetailsRequired =
    useGetIsCompoundingReasonDetailsRequired()(compoundingReasonIdField.value)

  // Filter out the incorrect compounding reason, and ensure the other is at the top of the list
  const excludeId = isRhapUpdateReasonForCompoundingEnabled
    ? DOSAGE_FORM_ID
    : DOSAGE_FORM_V2_ID
  const isPriority = R.pipe(
    R.prop('id'),
    R.includes(R.__, [DOSAGE_FORM_V2_ID, DOSAGE_FORM_ID]),
  )

  const sortedCompoundingReasons = R.pipe(
    R.reject(R.propEq('id', excludeId)),
    R.partition(isPriority),
    R.unnest,
  )(prescriptionCompoundingReason)

  return (
    <Grid
      container
      item
      alignItems="center"
      direction="column"
      pl={2}
      spacing={2}
    >
      <FormControl fullWidth margin="normal">
        <InputLabel>
          {t('Common:REASON_FOR_COMPOUNDING.REASON_FOR_COMPOUNDING')}*
        </InputLabel>
        <PuiSelect
          field={compoundingReasonIdField}
          items={sortedCompoundingReasons}
        />
      </FormControl>
      {isCompoundingReasonDetailsRequired && (
        <PuiTextField
          field={compoundingReasonField}
          label={`${
            isAllergiesIntolerances
              ? t(
                  'Common:REASON_FOR_COMPOUNDING.INPUT_LABEL_ALLERGIES_INTOLERANCES',
                )
              : t('Common:REASON_FOR_COMPOUNDING.INPUT_LABEL_OTHER')
          }*`}
        />
      )}
    </Grid>
  )
}

export default CompoundingReason
